/* @jsx h */
import {
  defineComponent,
  h,
  onMounted,
  onUnmounted,
  ref,
  type PropType,
} from "vue";
import type iro from "@jaames/iro";

const STORAGE_KEY = "lg-color-history";
const MAX_COLORS = 20;

function loadHistory(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function addToHistory(color: string, prev: string[]): string[] {
  const norm = color.toLowerCase();
  const next = [norm, ...prev.filter((c) => c !== norm)].slice(0, MAX_COLORS);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {}
  return next;
}

export const ColorPicker = defineComponent({
  name: "ColorPicker",
  props: {
    value: { type: String as PropType<string | null>, default: null },
    onChange: {
      type: Function as PropType<(color: string) => void>,
      required: true,
    },
    /** Suggested swatches (e.g. colors already in use at this hierarchy level and its parent level), shown above the "recent" history. */
    presetColors: { type: Array as PropType<string[]>, default: () => [] },
  },
  setup(props) {
    const wheelRef = ref<HTMLDivElement>();
    const history = ref<string[]>(loadHistory());
    const current = ref(props.value ?? "#3b82f6");
    let picker: iro.ColorPicker | null = null;
    let unmounted = false;

    onMounted(async () => {
      const { default: iroRuntime } = await import("@jaames/iro");
      if (unmounted) return;
      if (!wheelRef.value) return;
      picker = new (iroRuntime.ColorPicker as any)(wheelRef.value, {
        width: 180,
        color: current.value,
        borderWidth: 1,
        borderColor: "oklch(var(--bc) / 0.15)",
        layout: [
          { component: iroRuntime.ui.Wheel },
          {
            component: iroRuntime.ui.Slider,
            options: { sliderType: "value" },
          },
        ],
      });

      picker!.on("color:change", (color: iro.Color) => {
        current.value = color.hexString;
      });

      picker!.on("input:end", (color: iro.Color) => {
        const hex = color.hexString.toLowerCase();
        current.value = hex;
        history.value = addToHistory(hex, history.value);
        props.onChange(hex);
      });
    });

    onUnmounted(() => {
      unmounted = true;
      picker = null;
    });

    const pickColor = (color: string) => {
      if (picker) (picker as any).color.hexString = color;
      current.value = color;
      history.value = addToHistory(color, history.value);
      props.onChange(color);
    };

    return () => {
      const normalizedPresets = Array.from(
        new Set(props.presetColors.map((c) => c.toLowerCase())),
      );

      return (
        <div class="cp-wrapper">
          <div ref={wheelRef} class="cp-wheel" />
          {normalizedPresets.length > 0 && (
            <div class="cp-palette-section">
              <div class="cp-palette-label">Suggested</div>
              <div class="cp-palette">
                {normalizedPresets.map((c) => (
                  <button
                    key={c}
                    class={[
                      "cp-swatch",
                      current.value === c ? "cp-swatch--active" : "",
                    ]}
                    style={{ backgroundColor: c }}
                    title={c}
                    onClick={() => pickColor(c)}
                  />
                ))}
              </div>
            </div>
          )}
          {history.value.length > 0 && (
            <div class="cp-palette-section">
              <div class="cp-palette-label">Recent</div>
              <div class="cp-palette">
                {history.value.map((c) => (
                  <button
                    key={c}
                    class={[
                      "cp-swatch",
                      current.value === c ? "cp-swatch--active" : "",
                    ]}
                    style={{ backgroundColor: c }}
                    title={c}
                    onClick={() => pickColor(c)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      );
    };
  },
});
